import { useState } from '#app'
import { computed, type Component } from 'vue'
import type { MenuItem } from 'primevue/menuitem'
import { defaultImageValues, defaultLinkValues, defaultVideoValues, type CmsComponentData, type CmsData } from '../types/cmsTypes'
import { getcurrentChosenElement } from '../plugins/cms-components-list-block'
import { getByPath, setByPath } from '../utils/cmsDataHelper'
import CmsImage from '../components/cms/CmsImage.vue'
import CmsLink from '../components/cms/CmsLink.vue'
import CmsText from '../components/cms/CmsText.vue'
import CmsVideo from '../components/cms/CmsVideo.vue'
import { useCmsEditStore } from '../stores/cmsEditStore'
import { getIsGlobal } from '../plugins/cms-custom-component-list'

export const useCustomPageElementsActions = () => {
  const cmsData = useState<CmsData>('cmsData')
  const editStore = useCmsEditStore()

  const move_element_down = (index: number) => {
    const el = getcurrentChosenElement()
    const global = getIsGlobal()
    if (!el) return

    const path = [...el, 'component_data']

    const existing = getByPath(editStore.getAll(global) as CmsData, path)
    const current = Array.isArray(existing) ? existing : Object.values(existing || {})
    if (index < 0 || index >= current.length - 1) return
    [current[index], current[index + 1]] = [current[index + 1]!, current[index]!]
    setByPath(cmsData.value, path, JSON.parse(JSON.stringify(current)))
    editStore.set(path, JSON.parse(JSON.stringify(current)), global)
  }

  const move_element_up = (index: number) => {
    const el = getcurrentChosenElement()
    const global = getIsGlobal()
    if (!el) return

    const path = [...el, 'component_data']
    const existing = getByPath(editStore.getAll(global) as CmsData, path)

    const current = Array.isArray(existing)
      ? [...existing]
      : Object.values(existing || {})

    if (index <= 0 || index >= current.length) return

    [current[index - 1], current[index]] = [current[index]!, current[index - 1]!]
    setByPath(cmsData.value, path, JSON.parse(JSON.stringify(current)))
    editStore.set(path, JSON.parse(JSON.stringify(current)), global)
  }

  const remove_element = (index: number) => {
    const el = getcurrentChosenElement()
    const global = getIsGlobal()
    if (!el) return
    const path = [...el, 'component_data']

    const existing = getByPath(editStore.getAll(global) as CmsData, path)
    const current = Array.isArray(existing) ? existing : Object.values(existing || {})
    if (index < 0 || index >= current.length) return
    current.splice(index, 1)
    setByPath(cmsData.value, path, JSON.parse(JSON.stringify(current)))
    editStore.set(path, JSON.parse(JSON.stringify(current)), global)
  }

  const duplicate_element = (index: number) => {
    const el = getcurrentChosenElement()
    const global = getIsGlobal()
    if (!el) return

    const path = [...el, 'component_data']

    const existing = getByPath(editStore.getAll(global) as CmsData, path)
    const current = Array.isArray(existing) ? existing : Object.values(existing || {})
    current.splice(index + 1, 0, { ...(current)[index]! })
    setByPath(cmsData.value, path, JSON.parse(JSON.stringify(current)))
    editStore.set(path, JSON.parse(JSON.stringify(current)), global)
  }

  const add_element = (component_name: string, default_values: CmsComponentData) => {
    const el = getcurrentChosenElement()
    const global = getIsGlobal()
    if (!el) return
    const path = [...el, 'component_data']

    const existing = getByPath(editStore.getAll(global) as CmsData, path)

    const current = Array.isArray(existing) ? existing : Object.values(existing || {})

    const new_data = [
      ...current,
      {
        component_type: component_name,
        component_data: default_values
      }
    ]

    setByPath(cmsData.value, path, JSON.parse(JSON.stringify(new_data)))
    editStore.set(path, JSON.parse(JSON.stringify(new_data)), global)
  }

  const add_element_from_custom_components = (component_name: string) => {
    const el = getcurrentChosenElement()
    const global = getIsGlobal()
    if (!el) return

    const customComponentsList = getCustomComponentsList()

    if (!(component_name in customComponentsList)) {
      return
    }

    const path = [...el, 'component_data']

    const existing = getByPath(editStore.getAll(global) as CmsData, path)
    const current = Array.isArray(existing) ? existing : Object.values(existing || {})

    const new_data = [
      ...current,
      {
        component_type: component_name,
        component_data: {}
      }
    ]

    setByPath(cmsData.value, path, JSON.parse(JSON.stringify(new_data)))
    editStore.set(path, JSON.parse(JSON.stringify(new_data)), global)
  }

  const getCustomComponentsList = () => {
    return Object.fromEntries(
      Object.entries(allComponentModules).map(([path]) => {
        const relativePath = path.split('/CustomComponents/')[1]?.replace('.vue', '') || ''
        return [relativePath, { path }]
      })
    )
  }

  const custom_component_items = computed<MenuItem[]>(() => {
    const components = getCustomComponentsList()
    const categories: Record<string, MenuItem[]> = {}

    for (const [relativePath, { path }] of Object.entries(components)) {
      const name = path.split('/').pop()?.replace('.vue', '') || ''
      const parts = path.split('/CustomComponents/')[1]?.split('/') || []
      const category = parts.length > 1 ? parts[0]! : null

      const item: MenuItem = { label: name, command: () => add_element_from_custom_components(relativePath) }

      if (category) {
        if (!categories[category]) categories[category] = []
        categories[category].push(item)
      } else {
        categories['__root__'] = categories['__root__'] || []
        categories['__root__'].push(item)
      }
    }

    const result: MenuItem[] = []
    for (const [cat, items] of Object.entries(categories)) {
      if (cat === '__root__') result.push(...items)
      else result.push({ label: cat, items })
    }

    return result
  })

  const allComponentModules = import.meta.glob<Component>('~/components/CustomComponents/**/*.vue', { eager: true })
  const CustomComponents = Object.fromEntries(
    Object.entries(allComponentModules).map(([path, mod]) => {
      const relativePath = path.split('/CustomComponents/')[1]?.replace('.vue', '') || ''
      return [relativePath, (mod as { default: object }).default]
    })
  )

  const allComponents = { CmsText, CmsImage, CmsLink, CmsVideo, ...CustomComponents }

  const customPageOptionsGlobalMenu: MenuItem = {
    label: 'Dodaj Komponent do strony',
    items: [
      {
        label: 'Standard',
        items: [
          { label: 'CmsText', command: () => add_element('CmsText', { value: 'Lorem Ipsum' }) },
          { label: 'CmsLink', command: () => add_element('CmsLink', defaultLinkValues) },
          { label: 'CmsImage', command: () => add_element('CmsImage', defaultImageValues) },
          { label: 'CmsVideo', command: () => add_element('CmsVideo', defaultVideoValues) }
        ]
      },
      {
        label: 'Custom components',
        items: custom_component_items.value
      }
    ]
  }

  return { customPageOptionsGlobalMenu, allComponents, getCustomComponentsList, add_element, remove_element, duplicate_element, move_element_down, move_element_up }
}
